import React, { useState, useEffect } from 'react';
import { Card, Avatar, Button, Badge, message, notification, Popover, Tag, Tooltip as AntdTooltip } from 'antd';
import { TrophyFilled } from '@ant-design/icons';
import FinalModal from '../Molecules/FinalModal';
import TeamDetailsModal from '../Molecules/TeamDetailsModal';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Table, Space, Tooltip } from 'antd';

interface UserCardProps {
    empdata: any;
    stage: any;
    tabular?: boolean;
}

const baseURL = process.env.REACT_APP_API_URL;
const UserCard: React.FC<UserCardProps> = ({ empdata, stage, tabular }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [disabledButtons, setDisabledButtons] = useState<{ [key: string]: boolean }>({});
    const [teamModal, setTeamModal] = useState<boolean>(false);
    const [teamData, setTeamData] = useState<any[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
    const [totalSupportsCount, setTotalSupportCount] = useState<number>(0);
    const [totalRejectCount, setTotalRejectCount] = useState<number>(0);
    const [totalNoVisibilityCount, setTotalNoVisibilityCount] = useState<number>(0);
    const [totalcount, setTotalCount] = useState<number>(0);
    const [dataFetched, setDataFetched] = useState(false);
    const [empFetchData, setEmpFetchData] = useState<any[]>([]);
    const [onHold, setOnHold] = useState<{ [key: string]: boolean }>({});
    const [voteStatus, setVoteStatus] = useState<any>();
    const [eltKey, setEltKey] = useState<string | null>(null);
    const [superAdminKey, setSuperAdminKey] = useState<number | null>(null);
    const [supportList, setSupportList] = useState<any[]>([]);
    const [rejectList, setRejectList] = useState<any[]>([]);
    const [noVisibilityList, setNoVisibilityList] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const { instance, accounts } = useMsal();
    const [localEmpData, setLocalEmpData] = useState<any[]>([]);
    const [eltNameMap, setEltNameMap] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        setEltKey(localStorage.getItem('eltEmailKey'));
        setSuperAdminKey(Number(localStorage.getItem('superAdminKey')));
    }, []);

    useEffect(() => {
        const fetchEltNames = async () => {
            try {
                const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
                const response = await axios.get(`${baseURL}/api/nominations/elt/list`, {
                    headers: { Authorization: `Bearer ${res.idToken}` }
                });
                const map: { [key: string]: string } = {};
                (response.data?.responseData || []).forEach((item: any) => {
                    if (item.eltEmailKey && item.displayName) {
                        map[item.eltEmailKey] = item.displayName;
                    }
                });
                setEltNameMap(map);
            } catch { }
        };
        if (accounts[0]) fetchEltNames();
    }, [instance, accounts]);

    // Function to calculate the total score for an employee
    const calculateTotalScore = (employee: any) => {
        let totalScore = 0;
        for (let i = 1; i <= 12; i++) {
            const scoreColumn = `elt${i}Score`;
            if (employee[scoreColumn] !== null && employee[scoreColumn] !== undefined) {
                totalScore += Number(employee[scoreColumn]);
            }
        }
        return totalScore;
    };
    
    useEffect(() => {
        // Only sort by ranking if in the appropriate stage
        if (stage === '7' || stage === '9') {
            // Create a copy, add total scores and sort
            const sortedData = [...empdata].map(employee => ({
                ...employee,
                totalScore: calculateTotalScore(employee)
            })).sort((a, b) => b.totalScore - a.totalScore); // Sort descending (highest score first)
            
            setLocalEmpData(sortedData);
        } else {
            // For other stages, no sorting needed
            setLocalEmpData(empdata);
        }
    }, [empdata, stage]);

    const getELTResponse = (eltKeys: any) => {
        switch (eltKeys) {
            case 'elt_1_email':
                return 'elt1Response';
            case 'elt_2_email':
                return 'elt2Response';
            case 'elt_3_email':
                return 'elt3Response';
            case 'elt_4_email':
                return 'elt4Response';
            case 'elt_5_email':
                return 'elt5Response';
            case 'elt_6_email':
                return 'elt6Response';
            case 'elt_7_email':
                return 'elt7Response';
            case 'elt_8_email':
                return 'elt8Response';
            case 'elt_9_email':
                return 'elt9Response';
            case 'elt_10_email':
                return 'elt10Response';
            case 'elt_11_email':
                return 'elt11Response';
            case 'elt_12_email':
                return 'elt12Response';
            default:
                return '';
        }
    };

    const getELTVoteWeight = (eltKeys: any) => {
        switch (eltKeys) {
            case 'elt_1_email':
                return 'elt1Score';
            case 'elt_2_email':
                return 'elt2Score';
            case 'elt_3_email':
                return 'elt3Score';
            case 'elt_4_email':
                return 'elt4Score';
            case 'elt_5_email':
                return 'elt5Score';
            case 'elt_6_email':
                return 'elt6Score';
            case 'elt_7_email':
                return 'elt7Score';
            case 'elt_8_email':
                return 'elt8Score';
            case 'elt_9_email':
                return 'elt9Score';
            case 'elt_10_email':
                return 'elt10Score';
            case 'elt_11_email':
                return 'elt11Score';
            case 'elt_12_email':
                return 'elt12Score';
            default:
                return '';
        };
    };

    const getELTName = (eltKeys: any) => {
        switch (eltKeys) {
            case 'elt_1_email': return 'elt1Name';
            case 'elt_2_email': return 'elt2Name';
            case 'elt_3_email': return 'elt3Name';
            case 'elt_4_email': return 'elt4Name';
            case 'elt_5_email': return 'elt5Name';
            case 'elt_6_email': return 'elt6Name';
            case 'elt_7_email': return 'elt7Name';
            case 'elt_8_email': return 'elt8Name';
            case 'elt_9_email': return 'elt9Name';
            case 'elt_10_email': return 'elt10Name';
            case 'elt_11_email': return 'elt11Name';
            case 'elt_12_email': return 'elt12Name';
            default: return '';
        }
    };
    const columns = [
        {
            title: stage === '6' || stage === '7' ? 'Nominee Name' : 'Team Name',
            dataIndex: stage === '6' || stage === '7' ? 'nomineeName' : 'teamName',
            key: 'name',
            width: 140,
            ellipsis: {
                showTitle: false,
            },
            render: (text: string, record: any) => (
                <AntdTooltip title={text} placement="topLeft">
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <strong style={{ 
                            maxWidth: '120px', 
                            display: 'block'
                        }}>{text.length > 15 ? text.substring(0, 15) + '...' : text}</strong>
                        {record.nominationCount > 1 && (
                            <Tag color="gold" icon={<TrophyFilled />} style={{ marginLeft: 4, fontWeight: 600, flexShrink: 0 }}>
                                {record.nominationCount}n
                            </Tag>
                        )}
                    </span>
                </AntdTooltip>
            ),
        },
        ...(stage === '6' || stage === '7'
            ? [
                // {
                //     title: 'Designation',
                //     dataIndex: 'nomineeDesignation',
                //     key: 'designation',
                // },
                {
                    title: 'Product Name',
                    dataIndex: 'nomineeProductName',
                    key: 'product',
                    render: (_: any, record: any) => (
                        <span>{record.nomineeProductName}</span>
                    ),
                },
                {
                    title: 'Business Unit',
                    dataIndex: 'nomineeUnit',
                    key: 'bu',
                },
            ]
            : []
        ),
        {
            title: 'Review',
            key: 'review',
            width: 80,
            render: (_: any, employee: any) => (
                <Button
                    type="primary"
                    shape="round"
                    onClick={() => openFinalModal(employee)}
                    size="small"
                    icon={<span style={{ fontSize: '10px', marginRight: '4px' }}>📋</span>}
                    style={{
                        fontSize: '12px',
                        padding: '0 12px',
                        height: '24px',
                        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(37, 117, 252, 0.2)',
                        transition: 'all 0.3s ease'
                    }}
                    className="review-button"
                    data-tour="review-button"
                    aria-label="Review nomination details"
                >
                    Review
                </Button>
            ),
        },
        ...(stage === '7' || stage === '9' ? [
            {
                title: 'Votes',
                key: 'votes',
                width: 180,
                render: (_: any, employee: any) => {
                    // Calculate votes for this employee
                    let supportCount = 0;
                    let rejectCount = 0;
                    let noVisibilityCount = 0;
                    
                    // Count votes for this specific employee
                    for (let i = 1; i <= 12; i++) {
                        const responseColumn = `elt${i}Response`;
                        if (employee[responseColumn] === 'Support') {
                            supportCount++;
                        } else if (employee[responseColumn] === 'Reject') {
                            rejectCount++;
                        } else if (employee[responseColumn] === 'No Visibility') {
                            noVisibilityCount++;
                        }
                    }

                    // List of reviewers for this employee
                    const getEltDisplayName = (idx: number) =>
                        employee[`elt${idx}Name`] || eltNameMap[`elt_${idx}_email`] || employee[`elt${idx}Email`] || null;

                    const supportList = Array.from({ length: 12 }, (_, i) => {
                        const idx = i + 1;
                        return employee[`elt${idx}Response`] === 'Support' ? getEltDisplayName(idx) : null;
                    }).filter(Boolean);

                    const rejectList = Array.from({ length: 12 }, (_, i) => {
                        const idx = i + 1;
                        return employee[`elt${idx}Response`] === 'Reject' ? getEltDisplayName(idx) : null;
                    }).filter(Boolean);

                    const noVisibilityList = Array.from({ length: 12 }, (_, i) => {
                        const idx = i + 1;
                        return employee[`elt${idx}Response`] === 'No Visibility' ? getEltDisplayName(idx) : null;
                    }).filter(Boolean);

                    const renderList = (list: string[]) => (
                        <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                            {list.length > 0 ? (
                                list.map((name: string, index: number) => (
                                    <div key={index} style={{ fontSize: '12px', padding: '2px 0' }}>{name}</div>
                                ))
                            ) : (
                                <div>No Members</div>
                            )}
                        </div>
                    );

                    return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '0 5px' }}>
                            <div style={{ textAlign: 'center', minWidth: '50px' }}>
                                <Popover 
                                    content={renderList(supportList)} 
                                    title="Supports"
                                    trigger="hover"
                                    mouseEnterDelay={0.3}
                                >
                                    <div data-role="vote-count" style={{ color: 'green', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>{supportCount}</div>
                                </Popover>
                            </div>
                            <div style={{ textAlign: 'center', minWidth: '50px' }}>
                                <Popover 
                                    content={renderList(rejectList)} 
                                    title="Rejects"
                                    trigger="hover"
                                    mouseEnterDelay={0.3}
                                >
                                    <div data-role="vote-count" style={{ color: 'red', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>{rejectCount}</div>
                                </Popover>
                            </div>
                            <div style={{ textAlign: 'center', minWidth: '50px' }}>
                                <Popover 
                                    content={renderList(noVisibilityList)} 
                                    title="No Visibility"
                                    trigger="hover"
                                    mouseEnterDelay={0.3}
                                >
                                    <div data-role="vote-count" style={{ color: 'gray', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>{noVisibilityCount}</div>
                                </Popover>
                            </div>
                        </div>
                    );
                },
                children: [
                    {
                        title: 'Support',
                        key: 'support',
                        align: 'center' as const,
                        width: 80,
                        render: (_: any, employee: any) => {
                            // Count support votes for this specific employee
                            let supportCount = 0;
                            // Gather names of supporters for this employee
                            const supportList: string[] = [];
                            
                            for (let i = 1; i <= 12; i++) {
                                if (employee[`elt${i}Response`] === 'Support') {
                                    supportCount++;
                                    const displayName = employee[`elt${i}Name`] || eltNameMap[`elt_${i}_email`] || employee[`elt${i}Email`];
                                    if (displayName) supportList.push(displayName);
                                }
                            }
                            
                            const renderList = (list: string[]) => (
                                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                                    {list.length > 0 ? (
                                        list.map((name: string, index: number) => (
                                            <div key={index} style={{ fontSize: '12px', padding: '2px 0' }}>{name}</div>
                                        ))
                                    ) : (
                                        <div>No Members</div>
                                    )}
                                </div>
                            );
                            
                            return (
                                <Popover 
                                    content={renderList(supportList)} 
                                    title="Supports"
                                    trigger="hover"
                                    mouseEnterDelay={0.3}
                                >
                                    <div data-role="vote-count" style={{ color: 'green', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>{supportCount}</div>
                                </Popover>
                            );
                        }
                    },
                    {
                        title: 'Reject',
                        key: 'reject',
                        align: 'center' as const,
                        width: 80,
                        render: (_: any, employee: any) => {
                            // Count reject votes for this specific employee
                            let rejectCount = 0;
                            // Gather names of rejecters for this employee
                            const rejectList: string[] = [];
                            
                            for (let i = 1; i <= 12; i++) {
                                if (employee[`elt${i}Response`] === 'Reject') {
                                    rejectCount++;
                                    const displayName = employee[`elt${i}Name`] || eltNameMap[`elt_${i}_email`] || employee[`elt${i}Email`];
                                    if (displayName) rejectList.push(displayName);
                                }
                            }
                            
                            const renderList = (list: string[]) => (
                                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                                    {list.length > 0 ? (
                                        list.map((name: string, index: number) => (
                                            <div key={index} style={{ fontSize: '12px', padding: '2px 0' }}>{name}</div>
                                        ))
                                    ) : (
                                        <div>No Members</div>
                                    )}
                                </div>
                            );
                            
                            return (
                                <Popover 
                                    content={renderList(rejectList)} 
                                    title="Rejects"
                                    trigger="hover"
                                    mouseEnterDelay={0.3}
                                >
                                    <div data-role="vote-count" style={{ color: 'red', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>{rejectCount}</div>
                                </Popover>
                            );
                        }
                    },
                    {
                        title: 'No Visibility',
                        key: 'novisibility',
                        align: 'center' as const,
                        width: 80,
                        render: (_: any, employee: any) => {
                            // Count no visibility votes for this specific employee
                            let noVisibilityCount = 0;
                            // Gather names of no visibility voters for this employee
                            const noVisibilityList: string[] = [];
                            
                            for (let i = 1; i <= 12; i++) {
                                if (employee[`elt${i}Response`] === 'No Visibility') {
                                    noVisibilityCount++;
                                    const displayName = employee[`elt${i}Name`] || eltNameMap[`elt_${i}_email`] || employee[`elt${i}Email`];
                                    if (displayName) noVisibilityList.push(displayName);
                                }
                            }
                            
                            const renderList = (list: string[]) => (
                                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                                    {list.length > 0 ? (
                                        list.map((name: string, index: number) => (
                                            <div key={index} style={{ fontSize: '12px', padding: '2px 0' }}>{name}</div>
                                        ))
                                    ) : (
                                        <div>No Members</div>
                                    )}
                                </div>
                            );
                            
                            return (
                                <Popover 
                                    content={renderList(noVisibilityList)} 
                                    title="No Visibility"
                                    trigger="hover"
                                    mouseEnterDelay={0.3}
                                >
                                    <div data-role="vote-count" style={{ color: 'gray', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>{noVisibilityCount}</div>
                                </Popover>
                            );
                        }
                    }
                ]
            },
            {
                title: 'Ranking',
                key: 'ranking',
                align: 'center' as const,
                width: 60,
                sorter: (a: any, b: any) => {
                    const scoreA = a.totalScore !== undefined ? a.totalScore : calculateTotalScore(a);
                    const scoreB = b.totalScore !== undefined ? b.totalScore : calculateTotalScore(b);
                    return scoreB - scoreA; // Default descending order (high to low)
                },
                defaultSortOrder: 'descend' as const,
                render: (_: any, employee: any) => {
                    // Use the consistent calculation function
                    const totalScore = employee.totalScore !== undefined ? employee.totalScore : calculateTotalScore(employee);
                    
                    return (
                        <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '12px' }}>
                            {totalScore}
                        </div>
                    );
                }
            }
        ] : []),
        {
            title: 'Actions',
            key: 'actions',
            align: 'center' as const,
            width: (stage === '7' || stage === '9') ? 180 : undefined,
            render: (_: any, employee: any) => {
                const status = employee.finalLevelApproverVote;
                const isApproved = status === 'Approved';
                const isRejected = status === 'Rejected';
                const isOnHold = status === 'On Hold';

                const eltResponse = employee[getELTResponse(eltKey)];
                const isSupport = eltResponse === 'Support';
                const isReject = eltResponse === 'Reject';
                const isNoVisibility = eltResponse === 'No Visibility';

                return (
                    <Space>
                        {(stage === '7' || stage === '9') && (
                            <>
                                <Tooltip title="Approve this nominee">
                                    <Button
                                        type={isApproved ? 'primary' : 'default'}
                                        size="small"
                                        onClick={() => handleFinalVote(employee.approvalId, 'Approved')}
                                        disabled={onHold[employee.approvalId] || disabledButtons[employee.approvalId]}
                                        style={{
                                            background: onHold[employee.approvalId] ? undefined : isApproved ? 'green' : disabledButtons[employee.approvalId] ? undefined : 'green',
                                            color: isApproved ? 'white' : disabledButtons[employee.approvalId] ? undefined : onHold[employee.approvalId] ? undefined : 'white',
                                            width: 80,
                                            minWidth: 80,
                                            maxWidth: 80,
                                            fontSize: '12px',
                                            padding: '0 8px',
                                            height: '24px'
                                        }}
                                    >
                                        {isApproved ? 'Approved' : 'Approve'}
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Reject this nominee">
                                    <Button
                                        type={isRejected ? 'primary' : 'default'}
                                        size="small"
                                        onClick={() => handleFinalVote(employee.approvalId, 'Rejected')}
                                        disabled={onHold[employee.approvalId] || disabledButtons[employee.approvalId]}
                                        style={{
                                            background: onHold[employee.approvalId] ? undefined : isRejected ? 'red' : disabledButtons[employee.approvalId] ? undefined : 'red',
                                            color: isRejected ? 'white' : disabledButtons[employee.approvalId] ? undefined : onHold[employee.approvalId] ? undefined : 'white',
                                            width: 80,
                                            minWidth: 80,
                                            maxWidth: 80,
                                            fontSize: '12px',
                                            padding: '0 8px',
                                            height: '24px'
                                        }}
                                    >
                                        {isRejected ? 'Rejected' : 'Reject'}
                                    </Button>
                                </Tooltip>
                                <Tooltip title={onHold[employee.approvalId] ? "Release from hold" : "Put on hold"}>
                                    <Button
                                        type={isOnHold ? 'primary' : 'default'}
                                        size="small"
                                        onClick={() => toggleOnHold(employee.approvalId)}
                                        disabled={disabledButtons[employee.approvalId]}
                                        style={{
                                            background: onHold[employee.approvalId] ? 'teal' : disabledButtons[employee.approvalId] ? undefined : 'teal',
                                            color: isOnHold ? 'white' : disabledButtons[employee.approvalId] ? undefined : 'white',
                                            width: 80,
                                            minWidth: 80,
                                            maxWidth: 80,
                                            fontSize: '12px',
                                            padding: '0 8px',
                                            height: '24px'
                                        }}
                                    >
                                        {onHold[employee.approvalId] ? 'Release' : 'On Hold'}
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                        {(stage === '6' || stage === '8') && (
                            <>
                                <Tooltip title="Support this nominee">
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => handleVote(employee.approvalId, 'Support')}
                                        disabled={disabledButtons[employee.approvalId]}
                                        style={{
                                            background: isSupport
                                                ? 'green'
                                                : disabledButtons[employee.approvalId]
                                                    ? undefined
                                                    : 'green',
                                            color: isSupport ? 'white' : disabledButtons[employee.approvalId] ? 'black' : 'white',
                                            width: 90,
                                            opacity: disabledButtons[employee.approvalId] && !isSupport ? 0.5 : 1
                                        }}
                                    >
                                        {isSupport ? 'Supported' : 'Support'}
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Reject this nominee">
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => handleVote(employee.approvalId, 'Reject')}
                                        disabled={disabledButtons[employee.approvalId]}
                                        style={{
                                            background: isReject
                                                ? 'red'
                                                : disabledButtons[employee.approvalId]
                                                    ? undefined
                                                    : 'red',
                                            color: isReject ? 'white' : disabledButtons[employee.approvalId] ? 'black' : 'white',
                                            width: 80,

                                            opacity: disabledButtons[employee.approvalId] && !isReject ? 0.5 : 1
                                        }}
                                    >
                                        {isReject ? 'Rejected' : 'Reject'}
                                    </Button>
                                </Tooltip>
                                <Tooltip title="No visibility on this nominee">
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => handleVote(employee.approvalId, 'No Visibility')}
                                        disabled={disabledButtons[employee.approvalId]}
                                        style={{
                                            background: isNoVisibility
                                                ? '#888'
                                                : disabledButtons[employee.approvalId]
                                                    ? undefined
                                                    : '#888',
                                            color: isNoVisibility ? 'white' : disabledButtons[employee.approvalId] ? 'black' : 'white',
                                            width: 100,
                                            opacity: disabledButtons[employee.approvalId] && !isNoVisibility ? 0.5 : 1
                                        }}
                                    >
                                        {isNoVisibility ? 'No Visibility' : 'No Visibility'}
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                    </Space>
                );
            },
        },
    ];

    // Add these handlers for bulk actions
    const handleBulkApprove = async () => {
        try {
            for (const key of selectedRowKeys) {
                const id = key.toString();
                await handleFinalVote(id, 'Approved');
            }
            setSelectedRowKeys([]);
            message.success(`Successfully approved ${selectedRowKeys.length} nominations`);
        } catch (error) {
            console.error('Error in bulk approval', error);
            message.error('Failed to complete bulk approval');
        }
    };

    const handleBulkReject = async () => {
        try {
            for (const key of selectedRowKeys) {
                const id = key.toString();
                await handleFinalVote(id, 'Rejected');
            }
            setSelectedRowKeys([]);
            message.error(`Rejected ${selectedRowKeys.length} nominations`);
        } catch (error) {
            console.error('Error in bulk rejection', error);
            message.error('Failed to complete bulk rejection');
        }
    };

    const handleBulkOnHold = () => {
        for (const key of selectedRowKeys) {
            const id = key.toString();
            if (!onHold[id]) {
                toggleOnHold(id);
            }
        }
        message.info(`${selectedRowKeys.length} nominations put on hold`);
        setSelectedRowKeys([]);
    };

    const handleBulkRelease = () => {
        for (const key of selectedRowKeys) {
            const id = key.toString();
            if (onHold[id]) {
                toggleOnHold(id);
            }
        }
        message.info(`${selectedRowKeys.length} nominations released from hold`);
        setSelectedRowKeys([]);
    };
    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
        getCheckboxProps: (record: any) => ({
            disabled: disabledButtons[record.approvalId], // Can't select rows that are already voted on
            name: record.approvalId,
        }),
    };
    // Add these new bulk action handlers for ELT reviews
    const handleBulkSupport = async () => {
        try {
            for (const key of selectedRowKeys) {
                const id = key.toString();
                await handleVote(id, 'Support');
            }
            setSelectedRowKeys([]);
            message.success(`Successfully supported ${selectedRowKeys.length} nominations`);
        } catch (error) {
            console.error('Error in bulk support', error);
            message.error('Failed to complete bulk support');
        }
    };

    const handleBulkRejectELT = async () => {
        try {
            for (const key of selectedRowKeys) {
                const id = key.toString();
                await handleVote(id, 'Reject');
            }
            setSelectedRowKeys([]);
            message.error(`Rejected ${selectedRowKeys.length} nominations`);
        } catch (error) {
            console.error('Error in bulk rejection', error);
            message.error('Failed to complete bulk rejection');
        }
    };

    const handleBulkNoVisibility = async () => {
        try {
            for (const key of selectedRowKeys) {
                const id = key.toString();
                await handleVote(id, 'No Visibility');
            }
            setSelectedRowKeys([]);
            message.info(`Marked ${selectedRowKeys.length} nominations as No Visibility`);
        } catch (error) {
            console.error('Error in bulk no visibility', error);
            message.error('Failed to complete bulk no visibility marking');
        }
    };
    const checkExistingResponse = async () => {
        try {
            let tempSupportList: any[] = [];
            let tempRejectList: any[] = [];
            let tempNoVisibilityList: any[] = [];
            const updatedDisabledButtons: { [key: string]: boolean } = {};

            // Process all items in empdata array, not just the first one
            empdata.forEach((item: any) => {
                if (stage === '6' || stage === '8') {
                    if (item[getELTResponse(eltKey)] !== null && item[getELTResponse(eltKey)] !== undefined) {
                        updatedDisabledButtons[item.approvalId] = true;
                    }
                }

                if (stage === '7' || stage === '9') {
                    if (item.finalLevelApproverVote !== null &&
                        item.finalLevelApproverVote !== undefined &&
                        item.finalLevelApproverVote !== 'Not Reviewed') {
                        updatedDisabledButtons[item.approvalId] = true;
                    }
                }
            });

            // For displaying details of the first item (if that's the intended behavior)
            if (empdata.length > 0) {
                setEmpFetchData([empdata[0]]);
                setVoteStatus(empdata[0].finalLevelApproverVote);

                // The rest of your code for counting votes and setting lists
                let totalSupportCount = 0;
                let totalRejectCount = 0;
                let totalNoVisibilityCount = 0;
                let totalAggregateSum = 0;

                for (let i = 1; i <= 12; i++) {
                    const responseColumn = `elt${i}Response`;
                    const displayName = empdata[0][`elt${i}Name`] || eltNameMap[`elt_${i}_email`] || empdata[0][`elt${i}Email`];

                    if (empdata[0][responseColumn] === 'Support') {
                        tempSupportList.push(displayName);
                    }
                    if (empdata[0][responseColumn] === 'Reject') {
                        tempRejectList.push(displayName);
                    }
                    if (empdata[0][responseColumn] === 'No Visibility') {
                        tempNoVisibilityList.push(displayName);
                    }

                    const supportCount = empdata.filter((nomineeResponse: any) =>
                        nomineeResponse[responseColumn] === 'Support').length;
                    const rejectCount = empdata.filter((nomineeResponse: any) =>
                        nomineeResponse[responseColumn] === 'Reject').length;
                    const noVisibilityCount = empdata.filter((nomineeResponse: any) =>
                        nomineeResponse[responseColumn] === 'No Visibility').length;

                    totalSupportCount += supportCount;
                    totalRejectCount += rejectCount;
                    totalNoVisibilityCount += noVisibilityCount;

                    // Calculate total score
                    const voteWeightColumn = `elt${i}Score`;
                    const value = empdata[0][voteWeightColumn];
                    if (value !== null && value !== undefined) {
                        totalAggregateSum += Number(value);
                    }
                }

                setSupportList(tempSupportList);
                setRejectList(tempRejectList);
                setNoVisibilityList(tempNoVisibilityList);
                setTotalSupportCount(totalSupportCount);
                setTotalRejectCount(totalRejectCount);
                setTotalNoVisibilityCount(totalNoVisibilityCount);
                setTotalCount(totalAggregateSum);
            }

            // Apply the disabled buttons state
            setDisabledButtons(updatedDisabledButtons);

        } catch (error) {
            console.error('Error while fetching response', error);
        }
    };

    const renderList = (list: any[]) => (
        <div>
            {list.length > 0 ? (
                list.map((name: any, index: any) => (
                    <div key={index}>{name}</div>
                ))) : (
                <div>No Members</div>
            )}
        </div>
    )


    useEffect(() => {
        if (eltKey || superAdminKey) {
            checkExistingResponse();
        }
    }, [eltKey, empdata, superAdminKey]);

    const handleCancel = () => {
        // Smooth transition for closing
        setIsModalVisible(false);
        
        // Reset body overflow after modal closes
        setTimeout(() => {
            document.body.style.overflow = 'auto';
            // Only clear the selected employee after the modal animation completes
            if (!teamModal) {
                setSelectedEmployee(null);
            }
        }, 300);
    };

    const getInitials = (name: string) => {
        const [firstName, lastName] = name.split(' ');
        return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    };

    const handleCancelTeam = () => {
        setTeamModal(false);
    };

    const handleVote = async (id: any, response: string) => {
        let voteWeight = -1;
        if (response === 'Support') {
            voteWeight = 1;
        }
        if (response === 'Reject' || response === 'No Visibility') voteWeight = 0;


        if (id && response && voteWeight >= 0 && getELTResponse !== null && getELTVoteWeight !== null) {
            try {
                const res = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0]
                });

                const voterName = accounts[0]?.name || accounts[0]?.username || '';
                const patchData = JSON.stringify([
                    {
                        op: 'replace',
                        path: `/${getELTResponse(eltKey)}`,
                        value: response
                    },
                    {
                        op: 'replace',
                        path: `/${getELTVoteWeight(eltKey)}`,
                        value: voteWeight
                    },
                    {
                        op: 'replace',
                        path: `/${getELTName(eltKey)}`,
                        value: voterName
                    }
                ]);
                await axios.patch(`${baseURL}/api/nominations/${id}`, patchData, {
                    headers: {
                        'Content-Type': 'application/json-patch+json',
                        Authorization: `Bearer ${res.idToken}`,
                    }
                });

                // Update disabled buttons state
                setDisabledButtons(prev => ({ ...prev, [id]: true }));

                // Update localEmpData so UI reflects the change immediately
                setLocalEmpData(prev =>
                    prev.map(emp =>
                        emp.approvalId === id
                            ? {
                                ...emp,
                                [getELTResponse(eltKey)]: response,
                                [getELTVoteWeight(eltKey)]: voteWeight,
                                [getELTName(eltKey)]: voterName
                            }
                            : emp
                    )
                );

                message.success('Your response has been recorded. Thank you!');
            } catch (error) {
                console.error('Error while updating the vote', error);
                message.error('Failed to record response. Please try again.');
            }
        }
    };

    const handleFinalVote = async (id: any, response: string) => {
        if (id && response) {
            if (tabular) {
                const idx = empdata.findIndex((emp: any) => emp.approvalId === id);
                if (idx !== -1) {
                    empdata[idx].finalLevelApproverVote = response;
                }
            }
            try {
                const res = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0]
                });

                const patchData = JSON.stringify([
                    {
                        op: 'replace',
                        path: 'finalLevelApproverVote',
                        value: response
                    }
                ]);
                setVoteStatus(response);
                await axios.patch(`${baseURL}/api/nominations/${id}`, patchData, {
                    headers: {
                        'Content-Type': 'application/json-patch+json',
                        Authorization: `Bearer ${res.idToken}`,

                    }
                });
                setDisabledButtons(prev => ({ ...prev, [id]: true }));
                if (response === 'Approved') {
                    notification.success({
                        message: `${response}`,
                        description: 'You have completed the review. Thank you!',
                        placement: 'top',
                    });
                }
                if (response === 'Rejected') {
                    notification.error({
                        message: `${response}`,
                        description: 'You have completed the review. Thank you!',
                        placement: 'top',
                    });
                }

            }
            catch (error) {
                console.error('Error while updating the vote', error);
                message.error('Failed to record response. Please try again.');
            }

        }
    };


    const openFinalModal = (employee: any) => {
        // First set the employee data
        setSelectedEmployee(employee);
        
        // Add a slight delay before showing the modal for better UX
        setTimeout(() => {
            setIsModalVisible(true);
            // Update document body to prevent scrolling when modal is open
            document.body.style.overflow = 'hidden';
        }, 100);

        // Track modal open for analytics (if needed)
        if (stage === '7' || stage === '9') {
            console.log('Final review modal opened for approval stage');
        } else {
            console.log('Review modal opened for ELT voting stage');
        }
    };

    const toggleOnHold = (approvalId: string) => {
        setOnHold(prevState => ({ ...prevState, [approvalId]: !prevState[approvalId] }));
    };

    return (
        <>
            <style>
                {`
                .bulk-actions {
                    margin-bottom: 16px;
                    display: flex;
                    gap: 8px;
                }
            .responsive-user-card {
                width: 100%;
                max-width: 500px;
                margin: 1rem 0 1rem 0; /* Remove auto-centering */
            }
            @media (max-width: 600px) {
                .responsive-user-card {
                    max-width: 98vw;
                    margin: 0.5rem auto;
                    padding: 0;
                }
                .responsive-user-card .ant-card-body {
                    padding: 12px !important;
                }
                .responsive-user-card .ant-btn {
                    font-size: 0.95em;
                    padding: 4px 8px;
                }
                .usercard-btn-row {
                    flex-direction: column !important;
                    gap: 8px;
                }
            }
            .usercard-btn-row {
                display: flex;
                justify-content: space-around;
                margin-top: 1rem;
                gap: 0.5rem;
                flex-wrap: wrap;
            }
            /* Table columns styling for votes and ranking */
            .ant-table-cell.ant-table-cell-with-append {
                background-color: #f6f8fa;
            }
            .ant-table-thead > tr > th.ant-table-cell-with-append {
                background-color: #e6f7ff;
                font-weight: bold;
            }
            .ant-table-tbody > tr > td {
                vertical-align: middle;
                padding: 8px !important;
                font-size: 13px;
            }
            .ant-table-thead > tr > th {
                padding: 8px !important;
                font-size: 13px;
            }
            /* Specific styling for votes columns */
            .ant-table-cell[title="Support"],
            .ant-table-cell[title="Reject"],
            .ant-table-cell[title="No Visibility"] {
                padding: 4px !important;
                width: 60px !important;
            }
            .ant-table-cell[title="Ranking"] {
                width: 60px !important;
            }
            .ant-table-cell[title="Votes"] {
                background-color: #e6f7ff;
            }
            /* Make actions column more compact */
            .ant-table-cell[title="Actions"] .ant-space {
                gap: 3px !important;
                justify-content: center !important;
                flex-wrap: wrap !important;
            }
            .ant-table-cell[title="Actions"] button {
                width: 68px !important;
                min-width: 68px !important;
                max-width: 68px !important;
                font-size: 11px !important;
                padding: 0 2px !important;
                margin: 2px !important;
            }
            /* Center Actions column header text */
            .ant-table-cell[title="Actions"] {
                text-align: center;
            }
            
            /* Nominee Name column styling */
            .ant-table-cell[title="Nominee Name"],
            .ant-table-cell[title="Team Name"] {
                width: 150px !important;
                max-width: 150px !important;
                overflow: hidden !important;
            }
            
            /* Additional styling for compact table */
            .compact-table .ant-table-thead > tr > th {
                background-color: #fafafa;
                height: 32px;
            }
            .compact-table .ant-table-tbody > tr > td {
                height: 40px;
            }
            .compact-table .ant-table-row:hover {
                background-color: #f0f7ff;
            }
            .compact-table .ant-table-thead > tr > th.ant-table-column-has-sorters:hover {
                background-color: #e6f7ff;
            }
            /* Hover hint for vote counts */
            .compact-table .ant-popover-content {
                min-width: 180px;
            }
            .compact-table [data-role="vote-count"] {
                cursor: pointer;
                position: relative;
                text-decoration: underline dotted;
                text-underline-offset: 2px;
            }
            
            /* Enhanced modal styling */
            :global(.ant-modal-mask) {
                backdrop-filter: blur(2px);
                background-color: rgba(0, 0, 0, 0.65) !important;
            }
            :global(.ant-modal) {
                top: 50px;
                width: 85% !important;
                max-width: 800px !important;
            }
            :global(.ant-modal-content) {
                box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
                border-radius: 8px !important;
            }
            :global(.ant-modal-header) {
                border-radius: 8px 8px 0 0 !important;
            }
            :global(.ant-modal-body) {
                max-height: 75vh;
                overflow: auto;
                padding: 24px;
            }
            :global(.ant-modal-footer) {
                border-radius: 0 0 8px 8px !important;
            }
            
            /* Modal animation */
            :global(.ant-modal-root) {
                --modal-duration: 0.3s;
            }
            :global(.ant-modal-mask) {
                animation-duration: var(--modal-duration);
            }
            :global(.ant-modal-wrap) .ant-modal {
                transform-origin: center;
                animation-duration: var(--modal-duration);
            }
            :global(.ant-modal-wrap) .ant-modal.zoom-enter {
                transform: scale(0.95);
                opacity: 0;
            }
            :global(.ant-modal-wrap) .ant-modal.zoom-enter-active {
                transform: scale(1);
                opacity: 1;
                transition: transform var(--modal-duration), opacity var(--modal-duration);
            }
            :global(.ant-modal-wrap) .ant-modal.zoom-exit {
                transform: scale(1);
                opacity: 1;
            }
            :global(.ant-modal-wrap) .ant-modal.zoom-exit-active {
                transform: scale(0.95);
                opacity: 0;
                transition: transform var(--modal-duration), opacity var(--modal-duration);
            }
            
            /* Styling for the review button */
            .review-button:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(37, 117, 252, 0.4) !important;
                background: linear-gradient(135deg, #7918d9 0%, #3987ff 100%) !important;
            }
            `}
            </style>

            {tabular ? (
                <>
                    {(stage === '7' || stage === '9') && selectedRowKeys.length > 0 && (
                        <div className="bulk-actions">
                            <span>Selected {selectedRowKeys.length} items: </span>
                            <Button
                                type="primary"
                                onClick={handleBulkApprove}
                                style={{ background: 'green' }}
                            >
                                Approve All
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleBulkReject}
                                style={{ background: 'red' }}
                            >
                                Reject All
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleBulkOnHold}
                                style={{ background: 'teal' }}
                            >
                                Put All On Hold
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleBulkRelease}
                                style={{ background: 'orange' }}
                            >
                                Release All
                            </Button>
                            <Button onClick={() => setSelectedRowKeys([])}>
                                Clear Selection
                            </Button>


                        </div>
                    )}
                    {(stage === '6' || stage === '8') && selectedRowKeys.length > 0 && (
                        <div className="bulk-actions">
                            <span>Selected {selectedRowKeys.length} items: </span>
                            <Button
                                type="primary"
                                onClick={handleBulkSupport}
                                style={{ background: 'green' }}
                            >
                                Support All
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleBulkRejectELT}
                                style={{ background: 'red' }}
                            >
                                Reject All
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleBulkNoVisibility}
                                style={{ background: 'gray' }}
                            >
                                No Visibility All
                            </Button>
                            <Button onClick={() => setSelectedRowKeys([])}>
                                Clear Selection
                            </Button>
                        </div>
                    )}

                    <Table
                        rowSelection={(stage === '7' || stage === '9' || stage === '6' || stage === '8') ? rowSelection : undefined}
                        dataSource={localEmpData}
                        columns={columns}
                        rowKey="approvalId"
                        pagination={false}
                        style={{ margin: '1rem 0.5rem' }}
                        bordered
                        scroll={{ x: true }}
                        size="small"
                        className="compact-table"
                        tableLayout="fixed"
                    />
                </>
            ) : (
                empdata.map((employee: any, index: any) => (
                    <div key={index} style={{ position: 'relative' }}>
                        {/* Tag in top-right corner if multiple nominations */}
                        {employee.nominationCount > 1 && (
                            <AntdTooltip title={`${employee.nominationCount} nominations for this nominee`}>
                                <Tag
                                    color="gold"
                                    icon={<TrophyFilled />}
                                    style={{
                                        position: 'absolute',
                                        top: 10,
                                        right: 10,
                                        zIndex: 2,
                                        fontWeight: 600,
                                        fontSize: 15,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                    }}
                                >
                                    {employee.nominationCount}n
                                </Tag>
                            </AntdTooltip>
                        )}
                        <Card
                            key={index}
                            className="responsive-user-card"
                            style={{
                                backgroundColor: onHold[employee.approvalId] ? 'lightgray' : 'white',
                            }}
                        >
                            <Card.Meta
                                avatar={(stage === '6' || stage === '7') ? (
                                    <Avatar style={{ backgroundColor: 'purple' }}>
                                        {getInitials(employee.nomineeName)}
                                    </Avatar>
                                ) : null}
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                        <AntdTooltip 
                                            title={(stage === '6' || stage === '7') ? employee.nomineeName : employee.teamName} 
                                            placement="topLeft"
                                        >
                                            <Button
                                                onClick={() => openFinalModal(employee)}
                                                style={{
                                                    cursor: 'pointer',
                                                    color: 'purple',
                                                    border: 'none',
                                                    background: 'none',
                                                    fontSize: '1rem',
                                                    textAlign: 'left',
                                                    width: '180px',
                                                    maxWidth: '180px',
                                                    display: 'block',
                                                    padding: 0,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'block',
                                                        width: '100%',
                                                    }}
                                                >
                                                    {(() => {
                                                        const name = (stage === '6' || stage === '7') ? employee.nomineeName : employee.teamName;
                                                        return name.length > 10 ? name.substring(0, 10) + '...' : name;
                                                    })()}
                                                </span>
                                            </Button>
                                        </AntdTooltip>
                                        <Button 
                                            type="primary" 
                                            shape="round" 
                                            onClick={() => openFinalModal(employee)}
                                            icon={<span style={{ fontSize: '10px', marginRight: '4px' }}>📋</span>}
                                            style={{
                                                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                                border: 'none',
                                                boxShadow: '0 2px 4px rgba(37, 117, 252, 0.2)',
                                            }}
                                            className="review-button"
                                        >
                                            Review
                                        </Button>
                                        {(stage === '7' || stage === '9') && (
                                            <Badge count={employee.badgeNumber} style={{ backgroundColor: '#f50', marginLeft: '1rem' }} />
                                        )}
                                    </div>
                                }
                                description={
                                    <div>
                                        <div>{employee.nomineeDesignation}</div>
                                        {/* Show product name for individual, nominator's product for team, default color */}
                                        {(stage === '6' || stage === '7') ? (
                                            <div>{employee.nomineeProductName && (
                                                <span> {employee.nomineeProductName}</span>
                                            )}</div>
                                        ) : (
                                            <div>{employee.nominatorProductName && (
                                                <span> {employee.nominatorProductName}</span>
                                            )}</div>
                                        )}
                                        <div>{employee.nomineeUnit}</div>
                                        {(stage === '7' || stage === '9') ? (
                                            <div>
                                                Status: <strong style={{ color: voteStatus === 'Approved' ? 'green' : voteStatus === 'Rejected' ? 'red' : 'grey' }}>{JSON.stringify(voteStatus)}</strong>
                                            </div>
                                        ) : null}
                                    </div>
                                }
                            />
                            <div className="usercard-btn-row">
                                {(stage === '6' || stage === '8') && (
                                    <>
                                        <Button type="primary" onClick={() => handleVote(employee.approvalId, 'Support')} disabled={disabledButtons[employee.approvalId]} style={{ background: disabledButtons[employee.approvalId] ? 'grey' : 'green', width: 'auto' }}>Support</Button>
                                        <Button type="primary" onClick={() => handleVote(employee.approvalId, 'Reject')} disabled={disabledButtons[employee.approvalId]} style={{ background: disabledButtons[employee.approvalId] ? 'grey' : 'red', width: 'auto' }}>Reject</Button>
                                        <Button type="primary" onClick={() => handleVote(employee.approvalId, 'No Visibility')} disabled={disabledButtons[employee.approvalId]} style={{ background: disabledButtons[employee.approvalId] ? 'grey' : 'gray', width: 'auto' }}>No Visibility</Button>
                                    </>
                                )}
                                {(stage === '7' || stage === '9') && (
                                    <>
                                        <Button type="primary" onClick={() => handleFinalVote(employee.approvalId, 'Approved')} disabled={onHold[employee.approvalId] || disabledButtons[employee.approvalId]} style={{ background: disabledButtons[employee.approvalId] ? 'grey' : 'green', width: '80px' }}>Approve</Button>
                                        <Button type="primary" onClick={() => handleFinalVote(employee.approvalId, 'Rejected')} disabled={onHold[employee.approvalId] || disabledButtons[employee.approvalId]} style={{ background: disabledButtons[employee.approvalId] ? 'grey' : 'red', width: '80px' }}>Reject</Button>
                                        <Button type="primary" onClick={() => toggleOnHold(employee.approvalId)} disabled={disabledButtons[employee.approvalId]} style={{ background: disabledButtons[employee.approvalId] ? 'grey' : 'teal', width: '80px' }}>
                                            {onHold[employee.approvalId] ? 'Release' : 'On Hold'}
                                        </Button>
                                    </>
                                )}
                            </div>
                            {(stage === '9' || stage === '7') && (
                                <div style={{ marginTop: '1rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-around', fontWeight: 'bold' }}>
                                        <div><strong>Support</strong></div>
                                        <div><strong>Reject</strong></div>
                                        <div><strong>No Visibility</strong></div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-around', fontWeight: '10px' }}>
                                        <Popover 
                                            content={renderList(supportList)} 
                                            title="Supports"
                                            trigger="hover"
                                            mouseEnterDelay={0.3}
                                        >
                                            <div data-role="vote-count" style={{ cursor: 'pointer', color: 'green', fontWeight: 'bold' }}>{totalSupportsCount}</div>
                                        </Popover>
                                        <Popover 
                                            content={renderList(rejectList)} 
                                            title="Rejects"
                                            trigger="hover"
                                            mouseEnterDelay={0.3}
                                        >
                                            <div data-role="vote-count" style={{ cursor: 'pointer', color: 'red', fontWeight: 'bold' }}>{totalRejectCount}</div>
                                        </Popover>
                                        <Popover 
                                            content={renderList(noVisibilityList)} 
                                            title="No Visibility"
                                            trigger="hover"
                                            mouseEnterDelay={0.3}
                                        >
                                            <div data-role="vote-count" style={{ cursor: 'pointer', color: 'gray', fontWeight: 'bold' }}>{totalNoVisibilityCount}</div>
                                        </Popover>
                                    </div>
                                    <div style={{ color: 'red', fontSize: '12px' }}> * Hover on the counts to see the Reviewers</div>
                                </div>
                            )}
                        </Card>
                    </div>
                ))
            )}

            {/* Enhanced Modal Presentations */}
            {selectedEmployee && (
                <>
                    <FinalModal
                        visible={isModalVisible}
                        onClose={handleCancel}
                        nominee={selectedEmployee}
                        empdata={selectedEmployee}
                    />
                    <TeamDetailsModal
                        visible={teamModal}
                        onCancel={handleCancelTeam}
                        empData={selectedEmployee}
                        teamData={teamData}
                    />
                </>
            )}
        </>
    );
};

export default UserCard;
